import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  InputNumber,
  AccountingFormLayout,
  SearchableSelect,
} from "components";
import { useReceipt, useCustomer } from "hooks";
import { Receipt, ReceiptForm, ReceiptItem } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { Plus, Trash2 } from "lucide-react";
import { debounce } from "utils/helpers/data_helpers";
import { sanitizeReceiptItems } from "utils/helpers/sanitizers";
import { parseNumberSafe, parseRateSafe } from "utils/helpers/accounting";
import {
  RECEIPT_REASON_OPTIONS,
  VOUCHER_STATUS_OPTIONS,
} from "constants/accountingReasons";

const todayString = () => new Date().toISOString().slice(0, 10);
const ATTACHMENT_SPLIT_REGEX = /[,\n]/;

const defaultFormValues: ReceiptForm = {
  receipt_code: "",
  receipt_date: todayString(),
  object_name: "",
  payer_name: "",
  address: "",
  description: "",
  attachments: [],
  original_voucher_no: "",
  export_invoice_id: undefined,
  items: [],
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const ReceiptDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createReceipt, updateReceipt } = useReceipt();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );

  const [form, setForm] = useState<ReceiptForm>(defaultFormValues);
  const [attachmentsInput, setAttachmentsInput] = useState<string[]>([]);
  const [attachmentsText, setAttachmentsText] = useState<string>("");
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [items, setItems] = useState<ReceiptItem[]>([]);

  useEffect(() => {
    if (!detailResponse?.data) return;
    const receipt = detailResponse.data as Receipt;
    const normalizedAttachments = normalizeAttachments(receipt.attachments);
    const sanitizedItems = sanitizeReceiptItems(receipt.items);
    setForm({
      receipt_code: receipt.receipt_code || "",
      receipt_date: receipt.receipt_date || todayString(),
      object_name: receipt.object_name || "",
      payer_name: receipt.payer_name || "",
      address: receipt.address || "",
      description: receipt.description || "",
      attachments: normalizedAttachments,
      original_voucher_no: receipt.original_voucher_no || "",
      export_invoice_id: receipt.export_invoice_id,
      items: sanitizedItems,
    });
    setExistingAttachments(normalizedAttachments);
    setAttachmentsInput(normalizedAttachments);
    setAttachmentsText(normalizedAttachments.join(", "));
    setItems(
      sanitizedItems.length
        ? sanitizedItems
        : [
            {
              amount: 0,
              partner_id: null,
              reason: "",
              vat_rate: undefined,
              pit_rate: undefined,
            },
          ],
    );
  }, [detailResponse]);

  useEffect(() => {
    if (isEdit) return;
    setForm(defaultFormValues);
    setItems([
      {
        amount: 0,
        partner_id: null,
        reason: "",
        vat_rate: undefined,
        pit_rate: undefined,
      },
    ]);
    setAttachmentsInput([]);
    setAttachmentsText("");
    setExistingAttachments([]);
  }, [isEdit]);

  const totalAmount = useMemo(
    () =>
      items.reduce((sum, item) => sum + parseNumberSafe(item.amount ?? 0), 0),
    [items],
  );

  const handleFormChange = useCallback((key: keyof ReceiptForm, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAddItem = useCallback(() => {
    setItems((prev) => [
      ...prev,
      {
        amount: 0,
        partner_id: null,
        reason: "",
        vat_rate: undefined,
        pit_rate: undefined,
      },
    ]);
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof ReceiptItem, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                [key]:
                  key === "amount"
                    ? parseNumberSafe(value)
                    : key === "vat_rate" || key === "pit_rate"
                      ? parseRateSafe(value)
                      : value,
              }
            : item,
        ),
      );
    },
    [],
  );

  const debouncedItemChange = useCallback(
    (index: number, key: keyof ReceiptItem, value: any) =>
      handleItemChange(index, key, value),
    [handleItemChange],
  );

  // Auto-fill debit/credit accounts when reason_code is selected
  const handleReasonCodeChange = useCallback(
    (index: number, reasonCode: string) => {
      const reasonOption = RECEIPT_REASON_OPTIONS.find(
        (opt) => opt.value === reasonCode,
      );
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                reason_code: reasonCode,
                reason: reasonOption?.label ?? item.reason,
                debit_account: reasonOption?.debitAccount ?? item.debit_account,
                credit_account:
                  reasonOption?.creditAccount ?? item.credit_account,
              }
            : item,
        ),
      );
    },
    [],
  );

  const debouncedAttachmentsParse = useMemo(
    () =>
      debounce((text: string) => {
        const parsed = text
          .split(ATTACHMENT_SPLIT_REGEX)
          .map((v) => v.trim())
          .filter(Boolean);
        setAttachmentsInput(parsed);
      }, 250),
    [],
  );

  useEffect(() => {
    debouncedAttachmentsParse(attachmentsText);
  }, [attachmentsText, debouncedAttachmentsParse]);

  // Convert customers to SearchableSelect options format
  const customerOptions = useMemo(
    () =>
      (customersData?.data as any[])?.map((c) => ({
        value: c.id,
        label: `${c.code}${c.name ? ` - ${c.name}` : ""}`,
        searchData: c.name,
      })) ?? [],
    [customersData?.data],
  );

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback((): ReceiptForm | null => {
    if (!form.receipt_date || !form.object_name || !form.payer_name) {
      SnackbarUtils.error(
        "Ngày phiếu thu, Đơn vị/Đối tượng và Người nộp là bắt buộc",
      );
      return null;
    }

    if (isEdit && !form.receipt_code) {
      SnackbarUtils.error("Mã phiếu thu là bắt buộc khi cập nhật");
      return null;
    }

    const normalizedItems = sanitizeReceiptItems(items);

    if (!normalizedItems.length || !normalizedItems[0].amount) {
      SnackbarUtils.error("Vui lòng nhập ít nhất một dòng chi tiết và số tiền");
      return null;
    }

    return {
      ...form,
      attachments:
        attachmentsInput.length > 0
          ? normalizeAttachments(attachmentsInput)
          : existingAttachments,
      items: normalizedItems,
    };
  }, [attachmentsInput, existingAttachments, form, isEdit, items]);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      const payload = buildPayload();
      if (!payload) return;

      if (isEdit && id) {
        updateReceipt.mutate(
          { id: +id, ...payload },
          {
            onSuccess: () => {
              navigate(ROUTES.ACCOUNTING.CASH_RECEIPT);
            },
          },
        );
        return;
      }

      createReceipt.mutate(payload, {
        onSuccess: () => {
          navigate(ROUTES.ACCOUNTING.CASH_RECEIPT);
        },
      });
    },
    [buildPayload, createReceipt, id, isEdit, navigate, updateReceipt],
  );

  const isSaving = createReceipt.isPending || updateReceipt.isPending;

  return (
    <AccountingFormLayout
      title="Phiếu thu"
      subtitle="Nhập thông tin chung và danh sách dòng chi tiết."
      isEdit={isEdit}
      isSaving={isSaving}
      isLoading={isFetchingDetail}
      backUrl={ROUTES.ACCOUNTING.CASH_RECEIPT}
      backLabel="Danh sách phiếu thu"
      onSubmit={handleSubmit}
      submitLabel="Lưu phiếu thu"
      showAddItemButton={true}
      onAddItem={handleAddItem}
      addItemLabel="Thêm dòng"
      totalAmount={totalAmount}
    >
      {/* General Information Card */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            Thông tin chung
          </h2>
        </div>
        <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Mã phiếu thu (tự sinh nếu để trống)
            </label>
            <input
              value={form.receipt_code}
              onChange={(e) => handleFormChange("receipt_code", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="PT00001 (tự sinh nếu không nhập)"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Ngày phiếu thu *
            </label>
            <input
              type="date"
              value={form.receipt_date}
              onChange={(e) => handleFormChange("receipt_date", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Đơn vị/Đối tượng *
            </label>
            <input
              value={form.object_name}
              onChange={(e) => handleFormChange("object_name", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Khách lẻ"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Người nộp *
            </label>
            <input
              value={form.payer_name}
              onChange={(e) => handleFormChange("payer_name", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nhập người nộp"
              required
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Trạng thái
            </label>
            <select
              value={form.status ?? "draft"}
              onChange={(e) => handleFormChange("status", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
            >
              {VOUCHER_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">Địa chỉ</label>
            <input
              value={form.address ?? ""}
              onChange={(e) => handleFormChange("address", e.target.value)}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nhập địa chỉ"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Kèm theo (nhiều tệp)
            </label>
            <div className="space-y-2">
              <input
                type="text"
                value={attachmentsText}
                onChange={(e) => setAttachmentsText(e.target.value)}
                placeholder="tệp1.png, tệp2.pdf"
                className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              />
              {(attachmentsInput.length > 0 ||
                existingAttachments.length > 0) && (
                <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                  <p className="text-xs font-semibold text-gray-600 mb-1">
                    Tệp đã chọn:
                  </p>
                  <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside">
                    {(attachmentsInput.length > 0
                      ? attachmentsInput
                      : existingAttachments
                    ).map((item, idx) => (
                      <li key={`${idx}`}>{item}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Số CT gốc
            </label>
            <input
              value={form.original_voucher_no ?? ""}
              onChange={(e) =>
                handleFormChange("original_voucher_no", e.target.value)
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nhập số CT gốc"
            />
          </div>
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700">
              Hóa đơn xuất (ID)
            </label>
            <input
              type="number"
              value={form.export_invoice_id ?? ""}
              onChange={(e) =>
                handleFormChange(
                  "export_invoice_id",
                  e.target.value ? Number(e.target.value) : undefined,
                )
              }
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nhập số HĐ xuất"
            />
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-sm font-medium text-gray-700">
              Nội dung
            </label>
            <textarea
              value={form.description ?? ""}
              onChange={(e) => handleFormChange("description", e.target.value)}
              rows={2}
              className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
              placeholder="Nội dung thu"
            />
          </div>
        </div>
      </div>

      {/* Details Table */}
      <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
          <h2 className="text-base font-semibold text-gray-900">
            Thông tin chi tiết
          </h2>
          <div className="flex items-center gap-3">
            <div className="text-right">
              <p className="text-xs text-gray-500">Tổng tiền</p>
              <p className="text-base font-semibold text-gray-900">
                {totalAmount.toLocaleString("vi-VN")} đ
              </p>
            </div>
            <button
              type="button"
              onClick={handleAddItem}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors"
            >
              <Plus className="h-4 w-4" />
              Thêm dòng
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-700">
              <tr>
                <th className="px-3 py-2 text-left font-semibold w-12">STT</th>
                <th className="px-3 py-2 text-right font-semibold min-w-[150px]">
                  Số tiền
                </th>
                <th className="px-3 py-2 text-left font-semibold min-w-[200px]">
                  Khách hàng/NCC
                </th>
                <th className="px-3 py-2 text-left font-semibold min-w-[200px]">
                  Lý do thu
                </th>
                <th className="px-3 py-2 text-left font-semibold w-24">
                  TK Nợ
                </th>
                <th className="px-3 py-2 text-left font-semibold w-24">
                  TK Có
                </th>
                <th className="px-3 py-2 text-left font-semibold w-24">
                  TK Nợ
                </th>
                <th className="px-3 py-2 text-left font-semibold w-24">
                  TK Có
                </th>
                <th className="px-3 py-2 text-left font-semibold w-20">
                  VAT %
                </th>
                <th className="px-3 py-2 text-left font-semibold w-20">
                  PIT %
                </th>
                <th className="px-3 py-2 text-center font-semibold w-20">
                  Thao tác
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {items.length ? (
                items.map((item, index) => (
                  <tr key={`item-${index}`} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                    <td className="px-3 py-2 text-right text-gray-900">
                      <InputNumber
                        value={item.amount ?? 0}
                        onValueChange={(values) =>
                          debouncedItemChange(
                            index,
                            "amount",
                            values.floatValue ?? 0,
                          )
                        }
                        className="h-9 w-full px-2 py-1 text-right text-sm"
                        suffix=" ₫"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <SearchableSelect
                        options={customerOptions}
                        value={item.partner_id}
                        onChange={(value) =>
                          debouncedItemChange(index, "partner_id", value)
                        }
                        placeholder="Chọn khách hàng/NCC"
                        clearable
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <select
                        value={item.reason_code ?? ""}
                        onChange={(e) =>
                          handleReasonCodeChange(index, e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                      >
                        <option value="">Chọn lý do</option>
                        {RECEIPT_REASON_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        value={item.debit_account ?? ""}
                        onChange={(e) =>
                          debouncedItemChange(
                            index,
                            "debit_account",
                            e.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        value={item.credit_account ?? ""}
                        onChange={(e) =>
                          debouncedItemChange(
                            index,
                            "credit_account",
                            e.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        type="number"
                        value={item.vat_rate ?? ""}
                        onChange={(e) =>
                          debouncedItemChange(index, "vat_rate", e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-3 py-2 text-gray-900">
                      <input
                        type="number"
                        value={item.pit_rate ?? ""}
                        onChange={(e) =>
                          debouncedItemChange(index, "pit_rate", e.target.value)
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-100"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-3 py-2 text-center">
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index)}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-40 disabled:cursor-not-allowed"
                        disabled={items.length === 1}
                      >
                        <Trash2 className="h-4 w-4" />
                        Xóa
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={7}
                    className="px-4 py-6 text-center text-gray-500"
                  >
                    Chưa có dòng chi tiết. Thêm mới để bắt đầu.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-end px-4 py-3 border-t border-gray-100 bg-gray-50">
          <p className="text-sm text-gray-600">
            Tổng cộng:{" "}
            <span className="font-semibold text-gray-900">
              {totalAmount.toLocaleString("vi-VN")} đ
            </span>
          </p>
        </div>
      </div>
    </AccountingFormLayout>
  );
};

export default ReceiptDetail;
